import type { Attraction, Event, FestivalSummary } from "./types";
import { getEventImage, hasOnlyFallbackImages, isOwnImage, type EventGroup } from "./utils";

export const EVENTS_PAGE_SIZE = 24;

export type EventFeedItem =
  | { type: "event"; group: EventGroup; sortDate: string }
  | { type: "festival"; festival: FestivalSummary; sortDate: string };

export function hasCardImagery(event: Event): boolean {
  return getEventImage(event) !== null;
}

/** An act the card can preview: it has a Spotify id that is not blocked. */
export function isPlayableAct(attraction: Attraction, blockedSpotifyIds: string[]): boolean {
  return !!attraction.spotify_id && !blockedSpotifyIds.includes(attraction.spotify_id);
}

/** Match the list tile's photo and primary artist on the server and client. */
export function getListCardPrimaryAttraction(event: Event, blockedSpotifyIds: string[]): Attraction | null {
  const pictured = event.attractions.filter((a) => a.image_url && isOwnImage(a.image_url));
  return pictured.find((a) => isPlayableAct(a, blockedSpotifyIds)) ?? pictured[0] ??
    event.attractions.find((a) => isPlayableAct(a, blockedSpotifyIds)) ?? event.attractions[0] ?? null;
}

export function getListCardImage(event: Event, blockedSpotifyIds: string[]): string | null {
  if (event.image_url && isOwnImage(event.image_url) && !hasOnlyFallbackImages(event)) return event.image_url;
  const primary = getListCardPrimaryAttraction(event, blockedSpotifyIds);
  return primary?.image_url && isOwnImage(primary.image_url) ? primary.image_url : null;
}

/** A bill with at least one playable act, which is what gives a card its play disc. */
export function hasPlayableAct(event: Event, blockedSpotifyIds: string[]): boolean {
  return event.attractions.some((a) => isPlayableAct(a, blockedSpotifyIds));
}

// Use the same order for the server preview and the complete client feed so
// image cards cannot jump ahead of already-rendered compact rows on hydration.
// Within a day the list leads with bills that can be previewed (they carry
// the play disc), image cards before compact rows in each half; the grid
// keeps its chronological order.
export function getEventFeedItems(
  groups: EventGroup[],
  festivals: FestivalSummary[],
  view: "list" | "grid",
  gridImageOnly = false,
  blockedSpotifyIds: string[] = [],
): EventFeedItem[] {
  const eventItems: EventFeedItem[] = groups
    .filter((group) => view !== "grid" || !gridImageOnly || hasCardImagery(group.events[0]))
    .map((group) => ({
      type: "event",
      group,
      sortDate: group.events[0].local_date || "9999-99-99",
    }));
  const festivalItems: EventFeedItem[] = festivals.map((festival) => ({
    type: "festival",
    festival,
    sortDate: festival.start_date || "9999-99-99",
  }));
  const rank = (item: EventFeedItem) => {
    if (view !== "list" || item.type !== "event") return 0;
    const event = item.group.events[0];
    return (hasPlayableAct(event, blockedSpotifyIds) ? 0 : 2) + (hasCardImagery(event) ? 0 : 1);
  };

  return [...festivalItems, ...eventItems].sort((a, b) =>
    a.sortDate.localeCompare(b.sortDate) || rank(a) - rank(b)
  );
}
